import {
  Box,
  Grid,
  Card,
  Stack,
  Button,
  Avatar,
  Typography,
  IconButton,
  useTheme
} from "@material-ui/core";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { grey } from "@material-ui/core/colors";
import { fNumber } from "utils/formatNumber";
import { ArrowForward, Call, OpenInNew, Share, WhatsApp } from "@material-ui/icons";
import {
  ListingCard,
  processedListingDataToListingCardProps
} from "../listings/public/ListingCard";
import { CarouselThumbnail } from "components/carousel/CarouselThumbnail";
import { VideoPlayer } from "components/VideoPlayer";
import { shareLink } from "utils/listings/helper";
import { ProcessedListingData } from "../../../@types/sharedSchema";
import {
  ListingAdditionalDetailsComponent,
  ListingBooleanPropertiesDetailsBag
} from "./ListingPropertiesAndAdditionalDetails";
import RoomsFloorAndSqrMtr from "./RoomsFloorAndSqrMtr";
import Label from "components/Label";
import { ListingCardLikes } from "./ListingCardLikes";
import { ListingInteractiveMap } from "../listings/modal/core/ListingInteractiveMap";
import { ListingLocationDetails } from "./ListingLocationDetails";
import {
  getListingMatchAdditionalDetails,
  parseListingAddressByDisplayMode
} from "utils/listings/display";
import type { FavoriteListingsOfContact, REST_schema } from "../../../@types/sharedSchema";
import type { CropToImageProps } from "components/Image";
import { MinisiteAiMakeoversSection } from "../makeovers/MinisiteAiMakeoversSection";

type ListingDetailsNewProps = CropToImageProps & {
  listing: ProcessedListingData;
  listingLink: string;
  customerData?: { name: string; avatarUrl: string; phoneNumber: string; profileLink: string };
  imagesHidden?: boolean;
  backButtonLink?: string;
  moreListingsData: { listing: ProcessedListingData; listingLink: string }[];
  filteredHiddenPictures?: boolean;
  favoriteListingsOfContact?: FavoriteListingsOfContact;
  /** AI-generated makeover images to showcase below the photo carousel (public minisite). */
  aiMakeoverImages?: REST_schema.PublicListingMakeoverImage[];
  /**
   * For non-webot listings: a link to the original listing on the source
   * portal. Rendered under the address as a small "Open in Yad2/Madlan"
   * button so it's discoverable without scrolling to the publisher card.
   */
  externalListingUrl?: string;
  externalListingSourceLabel?: string;
};

export function ListingDetailsNew({
  listing,
  listingLink,
  cropTopImage,
  customerData,
  imagesHidden,
  moreListingsData,
  backButtonLink = null,
  filteredHiddenPictures,
  favoriteListingsOfContact,
  aiMakeoverImages,
  externalListingUrl,
  externalListingSourceLabel
}: ListingDetailsNewProps): JSX.Element {
  const theme = useTheme();
  const { t } = useTranslation();

  let {
    tags,
    price,
    address,
    videoUrl,
    imageUrls,
    roomsCount,
    description,
    squareMeter,
    sizeOfPlotM2,
    addressDisplayMode,
    addressCoordinates
  } = listing;

  const { city, neighborhood, street, floor } =
    parseListingAddressByDisplayMode(address, addressDisplayMode) ?? {};

  const { name, avatarUrl, phoneNumber, profileLink } = customerData ?? {};
  const fullListingsLink = `${window.location.origin}${listingLink}`;

  const { hiddenPictures, descriptionOverride } = getListingMatchAdditionalDetails({
    listingId: listing.id,
    description: description,
    favoriteListingsOfContact: favoriteListingsOfContact
  });

  return (
    <Grid container spacing={2}>
      {backButtonLink && (
        <Grid item xs={12}>
          <Button
            component={Link}
            to={backButtonLink}
            variant="outlined"
            startIcon={<ArrowForward />}
          >
            {t("listingsDetails.backToListings")}
          </Button>
        </Grid>
      )}
      <Grid xs={12} md={8} item>
        <CarouselThumbnail
          inPublicListingPage
          pictures={imageUrls?.map((p) => ({ src: p, alt: "", id: p }))}
          imagesHidden={imagesHidden}
          cropTopImage={cropTopImage}
          hiddenPictures={hiddenPictures}
          filterHiddenPictures={filteredHiddenPictures}
        />
      </Grid>
      {aiMakeoverImages && aiMakeoverImages.length > 0 && (
        <Grid xs={12} md={8} item>
          <MinisiteAiMakeoversSection images={aiMakeoverImages} />
        </Grid>
      )}
      {videoUrl && (
        <Grid xs={12} md={8} item>
          <Stack spacing={1}>
            <Typography variant="h5">{t("listingsPage.propertyVideo")}</Typography>
            <Box sx={{ position: "relative", width: "100%", aspectRatio: "16 / 9" }}>
              <VideoPlayer url={videoUrl} />
            </Box>
          </Stack>
        </Grid>
      )}
      {customerData && (
        <Grid sx={{ order: { xs: 1, md: "initial" } }} xs={12} md={4} item>
          <Card sx={{ height: { md: 384 } }}>
            <Stack sx={{ height: "100%" }}>
              <Box sx={{ p: 1, flex: 1 }}>
                <Stack
                  spacing={1}
                  sx={{ height: "100%" }}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Box sx={{ p: 0.5, border: 2, borderRadius: "50%", borderColor: grey[200] }}>
                    <Avatar sx={{ width: 64, height: 64 }} src={avatarUrl} alt={name} />
                  </Box>
                  <Typography
                    variant="h5"
                    to={profileLink}
                    component={Link}
                    sx={{
                      color: theme.palette.text.primary,
                      textDecoration: "transparent",
                      transition: "text-decoration 200ms",
                      "&:hover": {
                        textDecoration: "underline"
                      }
                    }}
                  >
                    {name}
                  </Typography>
                  <Stack direction={"row"} spacing={2}>
                    {address.street && (
                      <IconButton
                        href={`https://wa.me/${phoneNumber}/?text=היי, יש לי שאלה לגבי הנכס ברחוב ${address.street}`}
                        target="_blank"
                      >
                        <WhatsApp color="success" />
                      </IconButton>
                    )}
                    {phoneNumber && (
                      <IconButton href={`tel:${phoneNumber}`} target="_blank">
                        <Call color="info" />
                      </IconButton>
                    )}
                  </Stack>
                  {profileLink && (
                    <Typography
                      sx={{
                        textDecoration: "transparent",
                        transition: "text-decoration 200ms",
                        "&:hover": {
                          textDecoration: "underline"
                        }
                      }}
                      align="center"
                      color={theme.palette.grey[600]}
                      fontWeight={500}
                      to={profileLink}
                      component={Link}
                    >
                      {t("listingsPage.viewProfile")}
                    </Typography>
                  )}
                </Stack>
              </Box>
              <Stack sx={{ p: 4, bgcolor: grey[200] }} spacing={2}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="secondary"
                      size="large"
                      startIcon={<Share />}
                      onClick={shareLink({
                        title: t("listingsPage.listingLink"),
                        url: fullListingsLink
                      })}
                    >
                      {t("listingsPage.control.share")}
                    </Button>
                  </Grid>
                </Grid>
              </Stack>
            </Stack>
          </Card>
        </Grid>
      )}
      <Grid xs={12} md={8} item>
        <Stack spacing={4}>
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="start" gap={1}>
              <Typography sx={{ color: grey[800], wordBreak: "break-word" }} variant="h4">
                {street ? street : ""}
                {neighborhood && (
                  <>
                    {street && ", "}
                    <Typography
                      sx={{ color: grey[700] }}
                      display="inline"
                      variant="h4"
                      component="span"
                    >
                      {neighborhood}
                    </Typography>
                  </>
                )}
                {city && (
                  <>
                    {", "}
                    <Typography
                      sx={{ color: grey[600] }}
                      display="inline"
                      variant="h4"
                      component="span"
                    >
                      {city}
                    </Typography>
                  </>
                )}
              </Typography>
              {favoriteListingsOfContact && favoriteListingsOfContact.contactId && (
                <ListingCardLikes
                  listingId={listing.id}
                  favoriteListingsOfContact={favoriteListingsOfContact}
                />
              )}
            </Stack>
            {externalListingUrl && (
              <Box sx={{ mt: 1 }}>
                <Button
                  component="a"
                  href={externalListingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<OpenInNew />}
                  variant="contained"
                  size="small"
                  sx={{
                    bgcolor: "warning.lighter",
                    color: "warning.darker",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "warning.light", boxShadow: "none" }
                  }}
                >
                  {externalListingSourceLabel
                    ? t("listingPreview.viewOnSource", { source: externalListingSourceLabel })
                    : t("listingPreview.openSource")}
                </Button>
              </Box>
            )}
            <Typography sx={{ color: "primary.main" }} variant="h4">
              {`₪${fNumber(price)}`}
            </Typography>
          </Box>
          <Box sx={{ mt: 1 }}>
            <RoomsFloorAndSqrMtr
              displaySize="regular"
              floor={floor}
              roomsCount={roomsCount}
              areaSizeM2={squareMeter}
              plotSizeM2={sizeOfPlotM2}
            />
          </Box>
          {tags && tags.length > 0 && (
            <Grid container spacing={1}>
              {tags.map((tag) => (
                <Grid item key={tag} sx={{ px: 1 }}>
                  <Label color="warning">{tag}</Label>
                </Grid>
              ))}
            </Grid>
          )}
          {descriptionOverride && (
            <Stack spacing={1}>
              <Typography variant="h5">{t("listingsPage.tableColumns.description")}</Typography>
              <Typography variant="body1" color={grey[600]} whiteSpace="pre-line" fontSize={18}>
                {descriptionOverride}
              </Typography>
            </Stack>
          )}
          <ListingLocationDetails listing={listing} />
          <ListingAdditionalDetailsComponent listing={listing} />
          <ListingBooleanPropertiesDetailsBag data={listing} />
        </Stack>
      </Grid>
      <Grid xs={12} md={4} item>
        <ListingInteractiveMap readonly addressCoordinates={addressCoordinates} />
      </Grid>
      {!!moreListingsData.length && (
        <Grid sx={{ order: { xs: 2, md: "initial" }, mt: 4 }} xs={12} item>
          <Stack gap={2}>
            <Stack spacing={1}>
              <Typography variant="h4">{t("listingsPage.moreListings")}</Typography>
              <Grid sx={{ ml: "-16px !important" }} container spacing={2}>
                {moreListingsData.map((listingRec) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={listingRec.listing.id}>
                    <ListingCard
                      {...processedListingDataToListingCardProps(
                        listingRec.listing,
                        listingRec.listingLink
                      )}
                      favoriteListingsOfContact={favoriteListingsOfContact}
                    />
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Stack>
        </Grid>
      )}
    </Grid>
  );
}
